'use client';

import { useEffect, useState } from 'react';
import { getImageUrl } from '@/lib/api';
import { API_PROXY_PATH, API_ENDPOINTS } from '@/lib/constants';
import HorizontalSmallImageCategoryNews from '../component_horizontal/HorizontalSmallImageCategoryNews';
import { FALLBACK_IMAGE } from '@/lib/constants';

interface Story {
  headline: string;
  'hero-image-s3-key': string | null;
  'author-name': string;
  slug: string;
  'published-at': number;
  'story-template': string;
}

interface StoryItem {
  story: Story;
}

interface TrendingTabProps {
  trendingStories?: StoryItem[];
  commentedStories?: StoryItem[];
}

const TrendingTab = ({ trendingStories: propTrendingStories = [], commentedStories: propCommentedStories = [] }: TrendingTabProps) => {
  const [activeTab, setActiveTab] = useState<'popular' | 'commented'>('popular');
  const [trendingStories, setTrendingStories] = useState<StoryItem[]>(propTrendingStories);
  const [commentedStories, setCommentedStories] = useState<StoryItem[]>(propCommentedStories);
  const [loading, setLoading] = useState(propTrendingStories.length === 0 && propCommentedStories.length === 0);
  const [hasFetched, setHasFetched] = useState(false);

  useEffect(() => {
    // If props are provided, don't fetch data
    if (propTrendingStories.length > 0 || propCommentedStories.length > 0) {
      setTrendingStories(propTrendingStories);
      setCommentedStories(propCommentedStories);
      setLoading(false);
      setHasFetched(true);
      return;
    }

    // Prevent multiple fetches
    if (hasFetched) return;

    const fetchData = async () => {
      try {
        setLoading(true);

        // Use constants for API calls
        const storyFields = 'headline,slug,hero-image-s3-key,hero-image-metadata,author-name,published-at,story-template';
        const [trendingRes, commentedRes] = await Promise.all([
          fetch(`${API_PROXY_PATH}${API_ENDPOINTS.TRENDING}?limit=5&fields=${storyFields}`),
          fetch(`${API_PROXY_PATH}${API_ENDPOINTS.KARUTHUK_KALAM}?story-fields=${storyFields}`),
        ]);

        const trendingData = await trendingRes.json();
        const commentedData = await commentedRes.json();

        // Trending: { stories: [...] }
        const trendingItems: StoryItem[] = (trendingData?.stories || [])
          .slice(0, 5)
          .map((story: Story) => ({ story }));

        // Commented: { items: [{ story: {...} }] }
        const commentedItems: StoryItem[] = (commentedData?.items || [])
          .slice(0, 5)
          .map((item: { story?: Story; item?: { story: Story } }) => ({
            story: item?.story || item?.item?.story || ({} as Story),
          }))
          .filter((item: StoryItem) => item.story?.headline);

        setTrendingStories(trendingItems);
        setCommentedStories(commentedItems);
        setHasFetched(true);
      } catch (error) {
        console.error('Failed to fetch stories:', error);
        setHasFetched(true);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [propTrendingStories, propCommentedStories, hasFetched]);

  const renderStories = (stories: StoryItem[]) => {
    if (loading) {
      return (
        <div className="py-4 space-y-3">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="animate-pulse flex gap-3 px-4">
              <div className="w-20 h-14 bg-gray-200 rounded"></div>
              <div className="flex-1 space-y-2">
                <div className="h-3 bg-gray-200 rounded w-3/4"></div>
                <div className="h-3 bg-gray-200 rounded w-1/2"></div>
              </div>
            </div>
          ))}
        </div>
      );
    }

    if (!stories || stories.length === 0) {
      return <div className="py-4 px-4">No stories found</div>;
    }

    return stories.map((item, index) => {
      const story = item.story;
      if (!story) return null;

      return (
        <div key={story.slug || index} className="border-b border-DCDCDC">
          <HorizontalSmallImageCategoryNews
            category={{ value: story['author-name'] || 'பொதுச்செய்தி' }}
            title={{ value: story.headline }}
            timeAgo={{ value: new Date(story['published-at']).toLocaleDateString('ta-IN') }}
            slug={story.slug}
            imageSrc={{ value: story['hero-image-s3-key'] ? getImageUrl(story['hero-image-s3-key']) : FALLBACK_IMAGE }}
            imageMetadata={(story as any)['hero-image-metadata']}
            templatetype={{ value: story['story-template'] || '' }}

          />
        </div>
      );
    });
  };

  return (
    <div className="w-full">
      <div className="flex border-b border-gray-200">
        <button
          type="button"
          onClick={() => setActiveTab('popular')}
          className={`py-3 px-4 text-12-tamil-900 cursor-pointer border-b-2 transition-colors ${
            activeTab === 'popular'
              ? 'text-[#215EF9] border-[#215EF9] font-bold'
              : 'text-[#4D4D4D] border-transparent'
          }`}
        >
          டிரெண்டிங்
        </button>
        {/* <button
          type="button"
          onClick={() => setActiveTab('commented')}
          className={`py-3 px-4 text-12-tamil-900 cursor-pointer border-b-2 transition-colors ${
            activeTab === 'commented'
              ? 'text-[#215EF9] border-[#215EF9] font-bold'
              : 'text-[#4D4D4D] border-transparent'
          }`}
        >
          அதிகம் கருத்து தெரிவிக்கப்பட்டது
        </button> */}
      </div>

      <div>
        {activeTab === 'popular' && renderStories(trendingStories)}
        {activeTab === 'commented' && renderStories(commentedStories)}
      </div>
    </div>
  );
};

export default TrendingTab;
